import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import {
  fetchProjects,
  addProject,
  editProject,
  deleteProjectById,
} from "../redux/actions/projectAction";
import { Project } from "../types/Project";
import Main from "../components/custom/Main";
import PageContainer from "../components/custom/PageContainer";
import Modal from "../components/Modal";
import CollapseCard from "../components/custom/CollapseCard";
import ProjectForm from "../components/ProjectForm";
import ProjectList from "../components/ProjectList";
import { StoreState } from "../redux/reducers";
import { useToast } from "../components/toast/ToastProvider";

const ProjectPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const projects = useSelector((state: StoreState) => state.project);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch on mount
  useEffect(() => {
    setLoading(true);
    dispatch(fetchProjects()).finally(() => setLoading(false));
  }, [dispatch]);

  // Handle Save
  const handleSave = async (data: Project) => {
    setLoading(true);
    try {
      if (selectedProject) {
        await dispatch(editProject(data.id, data));
        toast("Project updated successfully");
      } else {
        await dispatch(addProject(data));
        toast("Project added successfully");
      }
      await dispatch(fetchProjects());
      setSelectedProject(null);
      setShowModal(false);
    } catch (error) {
      toast("Error saving project");
    } finally {
      setLoading(false);
    }
  };

  // Handle Delete
  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      setLoading(true);
      try {
        await dispatch(deleteProjectById(id));
        toast("Project deleted successfully");
        await dispatch(fetchProjects());
      } catch (error) {
        toast("Error deleting project");
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle Edit
  const handleEdit = (item: Project) => {
    setSelectedProject(item);
    setShowModal(true);
  };

  return (
    <Main>
      <PageContainer
        title="Project Management"
        onButtonClick={() => setShowModal(true)}
        buttonText={loading ? "Loading..." : "ADD"}
      >
        <Modal
          visible={showModal}
          size="xl"
          onClose={() => {
            setShowModal(false);
            setSelectedProject(null);
          }}
          headerText={selectedProject ? "Update Project" : "Add Project"}
        >
          <ProjectForm
            initialData={selectedProject}
            onSave={handleSave}
            clearAfterSubmit={() => setSelectedProject(null)}
          />
        </Modal>

        <CollapseCard title="Project List">
          <ProjectList
            projects={projects}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CollapseCard>
      </PageContainer>
    </Main>
  );
};

export default ProjectPage;
